import { useData } from "../../../../../runtime/provider";
import React, { useMemo } from "react";
import { useGlobalSearchParams } from "expo-router";
import { Platform, ScrollView } from "react-native";
import { useLiveRoom } from "@hooks/useLiveRoom";
import { useArtists } from "@hooks/trpc/useArtists";
import LText from "@components/LText";
import Handle from "@components/Accesses/AccessDetails/components/common/Handle";
import BackgroundCircles from "@components/Accesses/AccessDetails/components/common/Background";
import LPressable from "@components/LPressable";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { theme } from "@theme";
import { useAccessViewport } from "@components/Accesses/AccessDetails/config";
import LBox from "@components/LBox";
import { useAuth } from "@utils/firebaseAuth/auth";
import type {
  FreeDrinkConfig,
  SerenadeTicketConfig,
  ChooseTheCoverConfig,
} from "@libs/types/access-live";
import LIcon from "@components/LIcon";
import { FadeInDown } from "react-native-reanimated";
import { trpc } from "@utils/trpc";

const LiveRoomActivityContent = ({
  accessId,
  activityId,
}: {
  accessId: string;
  activityId: string;
}) => {
  const session = useData();
  const { width: screenWidth } = useAccessViewport();
  const { liveRoomData, isLoading } = useLiveRoom(accessId);
  const { firebaseUser } = useAuth();
  const insets = useSafeAreaInsets();
  const castVote = trpc.user.liveRoom.castVote.useMutation({
    onSuccess: async () => {
      await session.utils.user.liveRoom.get.invalidate({ accessId });
    },
  });

  const activities = liveRoomData?.activities;
  const access = liveRoomData?.access;
  const firebaseUid = firebaseUser?.uid;

  const activity = useMemo(() => {
    // Show activity if room exists (not just when live, so users can view prizes)
    if (!activities) return null;
    return activities.find((a) => a.id === activityId) || null;
  }, [activities, activityId]);

  // Fetch artist data for displaying artist name
  const artistIds = useMemo(() => {
    if (!activity?.artist_id || !access) return [];
    const ids: string[] = [];
    if (activity.artist_id === access.artistId) {
      ids.push(activity.artist_id);
    } else {
      ids.push(activity.artist_id);
    }
    return ids;
  }, [activity, access]);

  const { query: artistsQuery } = useArtists(artistIds, {
    enabled: artistIds.length > 0,
  });
  const artists = artistsQuery.data || [];

  const isUserWinner = useMemo(() => {
    if (!firebaseUid || !activity) return false;
    return (
      activity.status === "completed" && activity.winners.includes(firebaseUid)
    );
  }, [firebaseUid, activity]);

  const isUserLoser = useMemo(() => {
    if (!firebaseUid || !activity) return false;
    // Only show loser screen for activities with winners (not choose_the_cover)
    if (activity.type === "choose_the_cover") return false;
    return (
      activity.status === "completed" && !activity.winners.includes(firebaseUid)
    );
  }, [firebaseUid, activity]);

  // Get user's current vote for choose_the_cover
  const userVote = useMemo(() => {
    if (!firebaseUid || !activity || activity.type !== "choose_the_cover")
      return null;
    return activity.votes?.[firebaseUid] || null;
  }, [firebaseUid, activity]);

  // Handle voting for choose_the_cover
  const handleVote = async (songOptionIndex: string) => {
    if (!accessId || !activity || !firebaseUser?.uid) return;
    if (activity.type !== "choose_the_cover" || activity.status !== "active")
      return;

    try {
      await castVote.mutateAsync({
        accessId,
        activityId,
        songOptionIndex,
      });
    } catch (error) {
      console.error("Error casting vote:", error);
    }
  };

  if (isLoading) {
    return (
      <LBox gap="s3">
        <LText variant="bodyL" color="greyText">
          Loading...
        </LText>
      </LBox>
    );
  }

  if (!activity) {
    return (
      <LBox gap="s3">
        <LBox bg="danger" padding="s3" borderRadius="m">
          <LText variant="bodyL" color="white">
            Activity not found
          </LText>
        </LBox>
      </LBox>
    );
  }

  return (
    <ScrollView
      contentContainerStyle={{
        backgroundColor: "transparent",
        paddingTop: theme.spacing.s4,
        gap: theme.spacing.s3,
        flexGrow: 1,
        width: screenWidth,
      }}
      showsVerticalScrollIndicator={false}
    >
      {Platform.OS !== "web" && <Handle />}
      <LBox gap="s4" paddingHorizontal="s4">
        <LText variant="h1" color="white">
          {activity.type === "free_drink"
            ? "Free Drink Giveaway"
            : activity.type === "serenade_ticket"
              ? "Serenade Ticket"
              : "Choose the Cover"}
        </LText>
        {activity.artist_id && liveRoomData?.access && (
          <LText variant="bodyM" color="whiteTranslucent70">
            {artists.find((a) => a.id === activity.artist_id)?.name ||
              "Unknown Artist"}
          </LText>
        )}
      </LBox>
      <LBox
        flex={1}
        borderWidth={2}
        borderColor="white"
        borderRadius="xl"
        style={{
          marginBottom: insets.bottom + theme.spacing.s3,
        }}
        overflow="hidden"
      >
        {/* Flash screens for activities with winners */}
        {activity.type !== "choose_the_cover" &&
          (isUserWinner || isUserLoser) && (
            <LBox
              flex={1}
              justifyContent="center"
              alignItems="center"
              style={{
                backgroundColor: isUserWinner ? "#00FF00" : "#AA0000",
              }}
              entering={FadeInDown}
            >
              <LText variant="Heading/H1/Bold" color="black" textAlign="center">
                {isUserWinner ? "🎉 YOU WON! 🎉" : "👎 YOU LOST! 👎"}
              </LText>
              <LText variant="h2" color="black" textAlign="center">
                {isUserWinner ? "Congratulations!" : "Better luck next time!"}
              </LText>
            </LBox>
          )}
        {/* Winning song display for choose_the_cover */}
        {activity.type === "choose_the_cover" &&
          activity.status === "completed" &&
          activity.winning_song && (
            <LBox
              flex={1}
              justifyContent="center"
              alignItems="center"
              bg="modalBackdrop"
            >
              <LBox alignItems="center" gap="s4" paddingHorizontal="s4">
                <LText
                  variant="Heading/H1/Bold"
                  color="white"
                  textAlign="center"
                >
                  🎵 Winning Song 🎵
                </LText>
                <LText variant="h1" color="white" textAlign="center">
                  {activity.winning_song}
                </LText>
              </LBox>
            </LBox>
          )}
        {activity.status === "pending" && (
          <LBox
            flex={1}
            bg="modalBackdrop"
            justifyContent="center"
            alignItems="center"
          >
            <LBox alignItems="center" gap="s4">
              <LText
                variant="Heading/H2/Medium"
                color="white"
                textAlign="center"
              >
                Waiting for activity to start...
              </LText>
            </LBox>
          </LBox>
        )}
        {activity.status === "active" &&
          activity.type !== "choose_the_cover" && (
            <LBox
              flex={1}
              bg="modalBackdrop"
              justifyContent="center"
              alignItems="center"
            >
              <LBox alignItems="center" gap="s4">
                <LText variant="bodyL" color="greyText">
                  Winners will be selected soon...
                </LText>
                <LText variant="bodyM" color="greyText">
                  {
                    (activity.config as FreeDrinkConfig | SerenadeTicketConfig)
                      .number_of_winners
                  }{" "}
                  {(activity.config as FreeDrinkConfig | SerenadeTicketConfig)
                    .number_of_winners === 1
                    ? "winner"
                    : "winners"}{" "}
                  will be selected
                </LText>
              </LBox>
            </LBox>
          )}
        {/* Voting interface for choose_the_cover */}
        {activity.status === "active" &&
          activity.type === "choose_the_cover" && (
            <LBox flex={1} bg="modalBackdrop" padding="s4" gap="s4">
              <LText
                variant="Heading/H2/Medium"
                color="white"
                textAlign="center"
              >
                Vote for your favorite song!
              </LText>
              <LBox gap="s3" flex={1}>
                {(activity.config as ChooseTheCoverConfig).song_options.map(
                  (song, index) => {
                    const songOptionId = index.toString();
                    const isSelected = userVote === songOptionId;
                    const voteCount = Object.values(
                      activity.votes || {},
                    ).filter((v) => v === songOptionId).length;

                    return (
                      <LPressable
                        key={index}
                        sentryEventName="vote_song_option_pressed"
                        onPress={() => handleVote(songOptionId)}
                      >
                        <LBox
                          backgroundColor="whiteTranslucent10"
                          padding="s4"
                          borderRadius="m-l"
                          borderWidth={2}
                          borderColor={
                            isSelected ? "white" : "whiteTranslucent20"
                          }
                          flexDirection="row"
                          alignItems="center"
                          justifyContent="space-between"
                          gap="s3"
                        >
                          <LBox flex={1}>
                            <LText variant="Body/B1/Medium" color="white">
                              {song}
                            </LText>
                            <LText
                              variant="Body/B2/Regular"
                              color="whiteTranslucent70"
                            >
                              {voteCount} vote{voteCount !== 1 ? "s" : ""}
                            </LText>
                          </LBox>
                          {isSelected && <LIcon name="tick_circle" size={24} />}
                        </LBox>
                      </LPressable>
                    );
                  },
                )}
              </LBox>
              {userVote !== null && (
                <LText
                  variant="Body/B2/Regular"
                  color="whiteTranslucent70"
                  textAlign="center"
                >
                  You can change your vote anytime before voting closes
                </LText>
              )}
            </LBox>
          )}
        {activity.status === "countdown" && (
          <LBox
            flex={1}
            bg="modalBackdrop"
            justifyContent="center"
            alignItems="center"
          >
            <LBox alignItems="center" gap="s4">
              <LText
                variant="Heading/H2/Medium"
                color="white"
                textAlign="center"
              >
                {activity.type === "choose_the_cover"
                  ? "Revealing winning song..."
                  : "Revealing winners..."}
              </LText>
              <LText
                color="white"
                fontSize={120}
                fontWeight="bold"
                lineHeight={140}
              >
                {activity.countdown_remaining ?? 0}
              </LText>
            </LBox>
          </LBox>
        )}
      </LBox>
    </ScrollView>
  );
};

export default function LiveRoomActivityScreen() {
  const { height: screenHeight } = useAccessViewport();
  const params = useGlobalSearchParams<{
    accessId: string;
    activityId: string;
  }>();

  const accessId = useMemo(() => {
    const id = params.accessId;
    return Array.isArray(id) ? id[0] : id || "";
  }, [params.accessId]);

  const activityId = useMemo(() => {
    const id = params.activityId;
    return Array.isArray(id) ? id[0] : id || "";
  }, [params.activityId]);

  if (!accessId || !activityId) {
    return null;
  }

  return (
    <LBox minHeight={screenHeight}>
      <BackgroundCircles imageUrl={null} />
      <LiveRoomActivityContent accessId={accessId} activityId={activityId} />
    </LBox>
  );
}
